import React from 'react';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { RootAction } from 'store/state';
import styled from '@emotion/styled';
import { H3 } from 'ds/components/typography';
import RadioGroup from 'ds/components/input/RadioGroup';
import { useField, useForm } from 'react-final-form-hooks';
import { withValidationMessage } from 'hocs/withValidationMessage';
import { TextField } from 'ds/components/input/TextField';
import { Button } from 'ds/components/button';
import { Dispatch } from 'redux';
import { inlineMutate } from 'store/state/mutationsResponse/actions';
import { UserFeedbackReason, IInvestorsFeedbackVariables, MutationType, InvestorsPortalPageType } from 'store/sagas/apiService/types';
import { Route, useRoute } from 'config/routes';
import { InvestorsFeedbackModalState } from 'store/state/app';
import Modal from 'ds/components/modal';

const WithValidationTextField = withValidationMessage(TextField);
const RadiogroupWithValidation = withValidationMessage(RadioGroup);

const Title = styled(H3)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

const Content = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(2, 5, 1.25),
  maxWidth: '376px',
  margin: '0 auto',
  button: {
    padding: theme.spacing(1.5, 3),
  },
}));


export interface DisclaimerFormFields {
  reason: UserFeedbackReason;
  message: string;
}

export interface DisclaimerFormValues {
  reason: UserFeedbackReason;
  message: string;
}

interface DisclaimerModalProps {
  onModalClose: (data: InvestorsFeedbackModalState) => void;
  onSubmit: (data: IInvestorsFeedbackVariables) => void;
}

function getInvestorsPageSourceToRoute(routeName: Route, isCompareHoodMode: boolean): InvestorsPortalPageType {
  const map = {
    [Route.InvestorsHomePage]: InvestorsPortalPageType.INVESTORS_HOMEPAGE,
    [Route.InvestorsNeighbourhoodPage]: isCompareHoodMode ? InvestorsPortalPageType.COMPARE_NEIGHBORHOODS_PAGE : InvestorsPortalPageType.NEIGHBORHOOD_PAGE,
    [Route.InvestorsWizardPage]: InvestorsPortalPageType.ONBOARDING_WIZARD,
    // TODO: add back when marketplace is live
    // [Route.]: InvestorsPortalPageType.INVESTORS_MARKETPLACE,
  };
  return map[routeName];
}

const DisclaimerModal: React.FC<Partial<DisclaimerModalProps>> = (props) => {
  const { t } = useLocale();
  const { route: { name, params } } = useRoute();
  const { onModalClose, onSubmit } = props;

  const { form, valid } = useForm<DisclaimerFormFields>({
    onSubmit: ({ reason, message }) => {
      onSubmit({ input: { reason, message, url: window.location.href, investorsPageSource: getInvestorsPageSourceToRoute(name, Boolean(params.compareId)) } });
    },
    validateOnBlur: true,
    validate: (values: DisclaimerFormValues) => {
      const { reason, message } = values;
      const messageLengthIsInRange = message && message.length > 1 && message.length <= 250;
      return {
        ...(reason ? null : { reason: t('feedback.popup.fieldNecessity') }),
        ...(messageLengthIsInRange ? null : { message: t('feedback.popup.fieldNecessity') }),
      };
    },
  });
  const radioButtonOptionField = useField('reason', form);
  const messageField = useField('message', form);

  const options = React.useMemo(() => (
    [ UserFeedbackReason.SUGGEST_IDEA, UserFeedbackReason.SOMETHING_IS_MISSING, UserFeedbackReason.REPORT_AN_ISSUE ].map(reason => ({
      label: t('investors.disclaimer.modal.reason.copy', { reason }), value: reason,
    }))
  ), [ t ]);

  return (
    <Modal mobileModalPositionBottom={false} isOpen onClose={() => onModalClose({ isPostFeedbackModalOpen: false, isModalOpen: false })}>
      <Content>
        <Title weight="bold">{t('investors.disclaimer.modal.subtitle.copy')}</Title>
        <RadiogroupWithValidation
          {...radioButtonOptionField.input}
          {...radioButtonOptionField.meta}
          options={options}
        />

        <WithValidationTextField
          multiline
          fullWidth
          charactersCounterMax={250}
          {...messageField.input}
          {...messageField.meta}
          placeholder={t('investors.disclaimer.form.message.placeholder.copy')}
          type="text"
          maxLength={250}
          rows={5}
        />
        <Button
          data-auto="feedback-submit-button"
          disabled={!valid}
          onClick={form.submit}
          fullWidth
          size="large"
        >
          {t('investors.disclaimer.form.submit.copy')}
        </Button>
      </Content>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onSubmit: (data: IInvestorsFeedbackVariables) => dispatch(inlineMutate(MutationType.InvestorsFeedback, data)),
});

export default connect(null, mapDispatchToProps)(DisclaimerModal);
